"use client";

import { useCallback, useRef, useState } from "react";

import { useDebugTrigger } from "@/features/best/product/promotion-popup/triggers/use-debug-trigger";
import { useMouseTopTrigger } from "@/features/best/product/promotion-popup/triggers/use-mouse-top-trigger";
import { useSessionDismiss } from "@/features/best/product/promotion-popup/triggers/use-session-dismiss";
import { useTimerTrigger } from "@/features/best/product/promotion-popup/triggers/use-timer-trigger";

type Options = {
  delayMs?: number;
  exitIntentThresholdPx?: number;
};

export function usePrimeDayPopup({
  delayMs = 15_000,
  exitIntentThresholdPx = 60,
}: Options = {}) {
  const [isOpen, setIsOpen] = useState(false);
  const hasTriggeredRef = useRef(false);
  const { isDismissed, dismiss } = useSessionDismiss("primeDayPopupDismissed");

  const enabled = !isOpen && !hasTriggeredRef.current && !isDismissed;

  const open = useCallback(() => {
    hasTriggeredRef.current = true;
    setIsOpen(true);
  }, []);

  const close = useCallback(() => {
    setIsOpen(false);
    dismiss();
  }, [dismiss]);

  const handleClickOut = useCallback(() => {
    dismiss();
    setIsOpen(false);
  }, [dismiss]);

  useTimerTrigger(open, { delayMs, enabled });
  useMouseTopTrigger(open, { thresholdPx: exitIntentThresholdPx, enabled });
  useDebugTrigger(open);

  return { isOpen, close, handleClickOut };
}
